/**
 * SecurityReportModal (C.2)
 *
 * Modal for generating and downloading security reports.
 * Supports: Security Summary, Login Activity, Audit Trail, Tunnel Performance.
 * Formats: CSV (all), PDF (summary only).
 */

import { useState } from 'react';
import { X, Download, FileText, Loader2 } from 'lucide-react';
import { generateSecurityReport } from '../../services/securityApi';

const REPORT_TYPES = [
  { value: 'security_summary', label: 'Security Summary' },
  { value: 'login_activity', label: 'Login Activity' },
  { value: 'audit_trail', label: 'Audit Trail' },
  { value: 'tunnel_performance', label: 'Tunnel Performance' },
] as const;

interface SecurityReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SecurityReportModal({ isOpen, onClose }: SecurityReportModalProps) {
  const [reportType, setReportType] = useState<string>('security_summary');
  const [format, setFormat] = useState<'csv' | 'pdf'>('csv');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PDF only available for summary
  const pdfAvailable = reportType === 'security_summary';

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const blob = await generateSecurityReport({
        type: reportType,
        format,
        start: new Date(startDate).toISOString(),
        end: new Date(endDate + 'T23:59:59').toISOString(),
      });

      // Download the blob
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'pdf' ? 'pdf' : 'csv';
      a.download = `${reportType}_${startDate}_${endDate}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ems-border">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-ems-green" />
            <h2 className="text-base font-semibold text-ems-text-primary">Generate Security Report</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-ems-bg-card-hover rounded">
            <X className="w-4 h-4 text-ems-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Report Type */}
          <div>
            <label className="block text-xs font-medium text-ems-text-secondary mb-1">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value);
                // Reset format if switching away from summary and PDF was selected
                if (e.target.value !== 'security_summary' && format === 'pdf') {
                  setFormat('csv');
                }
              }}
              className="w-full border border-ems-border rounded-lg px-3 py-2 text-sm bg-ems-bg text-ems-text-primary focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
            >
              {REPORT_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ems-text-secondary mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-ems-border rounded-lg px-3 py-2 text-sm bg-ems-bg text-ems-text-primary focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ems-text-secondary mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-ems-border rounded-lg px-3 py-2 text-sm bg-ems-bg text-ems-text-primary focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
              />
            </div>
          </div>

          {/* Format Selector */}
          <div>
            <label className="block text-xs font-medium text-ems-text-secondary mb-1">Format</label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={format === 'csv'}
                  onChange={() => setFormat('csv')}
                  className="text-ems-green focus:ring-ems-green"
                />
                <span className="text-sm text-ems-text-primary">CSV</span>
              </label>
              <label className={`flex items-center gap-2 ${pdfAvailable ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}>
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={format === 'pdf'}
                  onChange={() => pdfAvailable && setFormat('pdf')}
                  disabled={!pdfAvailable}
                  className="text-ems-green focus:ring-ems-green"
                />
                <span className="text-sm text-ems-text-primary">PDF</span>
                {!pdfAvailable && <span className="text-[10px] text-ems-text-muted">(Summary only)</span>}
              </label>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-ems-red/10 text-ems-red text-xs rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-ems-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-ems-text-secondary hover:bg-ems-bg-card-hover rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ems-green bg-ems-forest border border-ems-green/20 hover:bg-ems-forest/80 rounded-lg transition-colors disabled:opacity-50"
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isGenerating ? 'Generating...' : 'Generate & Download'}
          </button>
        </div>
      </div>
    </div>
  );
}
